import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';

/**
 * Ensure every section and every section-level attribute has a stable client
 * `id`, returning a NEW object (input is not mutated).
 *
 * WHY THIS EXISTS
 * ---------------
 * Attribute/section `id`s are a client-only concern (React keys, drag-and-drop,
 * selection). They are persisted opportunistically (`sections` is a Mongoose
 * `Mixed` column) but system-seeded templates — and older hand-authored ones —
 * can arrive from the server with attributes that have NO `id`. Two builder
 * components lazily backfill those ids on mount ([formSections.js] for sections,
 * [formBody.js] for the *active* section's attributes), each then trying to
 * re-capture the dirty-check baseline via `requestAnimationFrame`. Those two
 * rAF re-captures race and only cover a single section, so a freshly-opened
 * template with un-id'd attributes ends up with `formList !== initialFormListStr`
 * and reads as dirty even though the user never edited anything ("discard
 * changes?" on a pristine template).
 *
 * Normalizing ids deterministically ONCE, up front — before the baseline is
 * captured — makes `formList` stable on load: the lazy normalizers' `.some(!id)`
 * guards are already satisfied, so they never fire and never race. This is the
 * single source of truth for id backfill on the load path.
 *
 * Scope intentionally matches the two lazy normalizers exactly: section `id` and
 * section-level attribute `id`. Nested goal/target attributes are left alone
 * (no lazy normalizer touches them, so normalizing them here would have nothing
 * to stay consistent with).
 *
 * @param {Object} formData - Raw form/template object (has `.sections[]`)
 * @returns {Object} A cloned object with ids backfilled (or the input unchanged if no `.sections`)
 */
export const normalizeFormIds = (formData) => {
    if (!formData?.sections?.length) return formData;

    const normalized = cloneDeep(formData);
    normalized.sections = normalized.sections.map((section) => ({
        ...section,
        id: section?.id || uuidv4(),
        attributes: (section?.attributes || []).map((attr) => ({
            ...attr,
            id: attr?.id || uuidv4(),
        })),
    }));

    return normalized;
};
